import { useScreenType } from "component/common/screenType";
import Button from "component/ui/Button";
import Input from "component/ui/Input";
import TabSwitcher from "component/ui/Tabs";
import React, { useState, useEffect } from "react";
import { FaStar } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import {
  bookingAvailiabilityDetails,
  clearBookingAvailiabilityDetails,
  deleteVenueBooking,
  getBookingAvailiabilityList,
  getVenueAvailabilityDetails,
  getVenueSelectedSportsList,
  setVenueAvailability,
  updateVenueAvailability,
} from "store/slice/profile";
import { CurrencyFormat, getTimeZone } from "helper/appConstant";
import {
  Accordion,
  AccordionItem,
  AccordionItemHeading,
  AccordionItemButton,
  AccordionItemPanel,
} from "react-accessible-accordion";
import { MdEdit } from "react-icons/md";
import { MdDelete } from "react-icons/md";
import { IoIosArrowDown } from "react-icons/io";
import AddUpdateArena from "./addUpdateArena";
import HourSelect from "./common/hourSelect";
import FullPageLoader from "component/ui/fullPageLoader";
import { useNavigate, useSearchParams } from "react-router-dom";
import BackButton from "component/ui/BackButton";
import PageHeading from "component/ui/PageHeading";
import CommonModal from "component/ui/CommonModal";
import { ShieldQuestionMark } from "lucide-react";
import { TYPES } from "helper/constant";
import PageContainer from "component/ui/PageContainer";

const BookingHistory = ({ item, onEditClick, onDeleteClick }) => {
  const navigate = useNavigate();
  return (
    <Accordion allowZeroExpanded>
      <AccordionItem className="border border-gray-300 rounded-sm">
        <AccordionItemHeading>
          <AccordionItemButton className="flex items-center justify-between md:p-3 p-2 bg-[#f9f9f9]">
            <div className="grid grid-cols-6 md:grid-cols-7 items-center gap-2 md:gap-4 ">
              {/* Avatar */}
              <div className="h-8 w-8 md:h-12 md:w-12 rounded-full bg-gray-200 flex items-center justify-center shrink-0">
                <span className="text-lg md:text-xl">{item?.icon}</span>
              </div>

              {/* Title / text */}
              <div className="col-span-4 md:col-span-5 min-w-0">
                <div className="text-sm md:text-base font-medium truncate">
                  {item?.sportsName}
                </div>
              </div>

              {/* Actions */}
              <div className="col-span-1 md:col-span-1 flex items-center justify-end gap-1">
                <button
                  type="button"
                  className="p-1 rounded-md"
                  aria-label="Edit"
                >
                  <MdEdit
                    onClick={() => onEditClick(item?._id)}
                    className="w-4 h-4 md:w-6 md:h-6 text-[#2bace2]"
                  />
                </button>

                <button
                  type="button"
                  className="p-1 rounded-md"
                  aria-label="Delete"
                >
                  <MdDelete
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteClick(item?._id);
                    }}
                    className="w-4 h-4 md:w-6 md:h-6 text-red-500"
                  />
                </button>

                <button
                  type="button"
                  className="p-1 rounded-md"
                  aria-label="Toggle"
                >
                  <IoIosArrowDown className="w-4 h-4 md:w-6 md:h-6 text-[#2bace2]" />
                </button>
              </div>
            </div>
          </AccordionItemButton>
        </AccordionItemHeading>

        <AccordionItemPanel className="md:p-3 p-2">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-1 h-5 bg-gradient-to-b from-blue-500 to-indigo-600 rounded-full"></div>
            <p className="text-lg font-semibold text-gray-800">Arena Details</p>
          </div>

          <div className="space-y-3">
            {item?.courtDetails?.map((court, index) => (
              <div
                key={index}
                onClick={() =>
                  navigate(`/arena-details/${item?.userId}/${court._id}`, {
                    state: { court, arena: item },
                  })
                }
                className="bg-gradient-to-br from-white to-gray-50 border border-gray-200 md:p-3 px-1 py-2 rounded-xl shadow-sm hover:shadow-md transition-all duration-200"
              >
                <div className="space-y-2">
                  {/* Price */}
                  <div className="flex items-center justify-between pb-1 border-b border-gray-100">
                    <div className="flex items-center gap-2">
                      <svg
                        className="w-5 h-5 text-green-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <span className="font-medium text-gray-700 text-sm">
                        Price
                      </span>
                    </div>
                    <span className="text-sm font-medium text-green-600">
                      {court?.price && CurrencyFormat(court?.price)}
                    </span>
                  </div>

                  {/* Slot Frequency */}
                  <div className="flex items-center justify-between pb-2 border-b border-gray-100">
                    <div className="flex items-center gap-2">
                      <svg
                        className="w-5 h-5 text-blue-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <span className="font-medium text-gray-700 text-sm">
                        Slot Duration
                      </span>
                    </div>
                    <span className="text-sm font-medium text-gray-900 bg-blue-50 px-3 py-1 rounded-full">
                      {court?.slotFrequency} Minutes
                    </span>
                  </div>

                  {/* Reschedule/Cancel */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <svg
                        className="w-5 h-5 text-amber-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                      <span className="font-medium text-gray-700 text-sm">
                        Reschedule/Cancel Slot Frequency
                      </span>
                    </div>
                    <span className="text-sm font-medium text-gray-900 bg-amber-50 px-3 py-1 rounded-full">
                      {court?.rescheduleCancelFrequency} Hours
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </AccordionItemPanel>
      </AccordionItem>
    </Accordion>
  );
};
function VenueOperations() {
  const [venueType, setVenueType] = useState(1);
  const [showArenaForm, setShowArenaForm] = useState(false);
  const [sportData, setSportsData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const screenType = useScreenType();
  const [deleteId, setDeleteId] = useState("");
  const [showDeleteDialog, setDeleteDialog] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [sportsList, setSportsList] = useState([]);
  const [venueAvailabilityData, setVenueAvailabilityData] = useState({
    days: [
      { id: "Sunday", label: "Sun", isTrue: false },
      { id: "Monday", label: "Mon", isTrue: false },
      { id: "Tuesday", label: "Tue", isTrue: false },
      { id: "Wednesday", label: "Wed", isTrue: false },
      { id: "Thursday", label: "Thu", isTrue: false },
      { id: "Friday", label: "Fri", isTrue: false },
      { id: "Saturday", label: "Sat", isTrue: false },
    ],
  });
  const [searchParams] = useSearchParams();
  const type = searchParams.get("type");
  const {
    venueAvailabilityDetails,
    isUpdateVenueLoading,
    venueSelectedSportsData,
    bookingAvailiabilityData,
    isDeleteAccountLoading,
  } = useSelector((state) => state.profile);

  // set sports listing data
  useEffect(() => {
    if (venueSelectedSportsData) {
      setSportsList(venueSelectedSportsData);
    }
  }, [venueSelectedSportsData]);
  //   fetch venue availability data
  useEffect(() => {
    if (!venueAvailabilityDetails) {
      dispatch(getVenueAvailabilityDetails());
    }
  }, []);

  // set tab by type
  useEffect(() => {
    if (type) {
      setVenueType(type);
    }
  }, [type]);
  //   set venueAvailabilityDetails data
  useEffect(() => {
    if (venueAvailabilityDetails) {
      const updatedDays = venueAvailabilityData?.days?.map((day) => ({
        ...day,
        isTrue: venueAvailabilityDetails?.openingDays?.includes(day?.id), // set true if id matches
      }));
      setVenueAvailabilityData({
        ...venueAvailabilityDetails,
        days: updatedDays,
      });
    }
  }, [venueAvailabilityDetails]);
  // fetch booking availability list
  const fetchBookingAvailabiltyList = (page) => {
    let request = { language: "en", page: page ?? 1, perPage: 10 };
    dispatch(getBookingAvailiabilityList(request, setIsLoading));
  };
  // fetch sports list
  useEffect(() => {
    if (venueType == 2 && venueSelectedSportsData?.length == 0) {
      setIsLoading(true);
      dispatch(getVenueSelectedSportsList(setIsLoading));
    }
    if (venueType == 3 && !bookingAvailiabilityData) {
      setIsLoading(true);
      fetchBookingAvailabiltyList();
    }
  }, [venueType]);
  //   Venue Operation menu
  const VenueMenu = [
    {
      id: 1,
      title: "Operational Hours",
      color: "bg-gradient-to-t from-[#F178B0] to-[#DB0C6E]",
      icon: <FaStar color="orange" size={25} />,
      border: venueType == 1,
      onClick: () => navigate("/venue-operations?type=1"),
    },
    {
      id: 2,
      title: "Select Sport",
      color: "bg-gradient-to-t from-[#B2BBFF] to-[#2BACE2]",
      icon: <FaStar color="orange" size={25} />,
      border: venueType == 2,
      onClick: () => navigate("/venue-operations?type=2"),
    },
    {
      id: 2,
      title: "Booking Availability",
      color: "bg-gradient-to-t from-[#B2BBFF] to-[#2BACE2]",
      icon: <FaStar color="orange" size={25} />,
      border: venueType == 3,
      onClick: () => navigate("/venue-operations?type=3"),
    },
  ];

  //   handle update Venue Availability Success
  const handleVenueAvailabilitySuccess = () => {
    dispatch(getVenueAvailabilityDetails());
  };

  //  update venue availability
  const handleUpdateVenueAvailiability = () => {
    let request = {
      language: "en",
      openingDays: venueAvailabilityData?.days
        ?.filter((day) => day.isTrue)
        .map((day) => day.id),
      openingTime: venueAvailabilityData?.openingTime,
      closingTime: venueAvailabilityData?.closingTime,
      timeZone: getTimeZone(),
    };
    if (venueAvailabilityData?.availabilityId) {
      dispatch(
        updateVenueAvailability(
          {
            ...request,
            availabilityId: venueAvailabilityData?.availabilityId,
          },
          handleVenueAvailabilitySuccess
        )
      );
    } else {
      dispatch(setVenueAvailability(request, handleVenueAvailabilitySuccess));
    }
  };

  // handle booking availability detail
  const handleBookingAvailabiltyDetail = (id) => {
    dispatch(bookingAvailiabilityDetails(id));
    setShowArenaForm(true);
  };

  // handle filter list
  const handleFilterSportsList = (value) => {
    if (!value) {
      setSportsList(venueSelectedSportsData);
    } else {
      const filteredData = venueSelectedSportsData?.filter((item) =>
        item.name.toLowerCase().includes(value.toLowerCase())
      );
      setSportsList(filteredData);
    }
  };

  // handle delete booking
  const handleDeleteBooking = () => {
    const request = {
      language: "en",
      id: deleteId,
      roomId: null,
      type: TYPES?.VENUE_DELETE_TYPE,
    };
    dispatch(deleteVenueBooking(request, setDeleteDialog));
  };
  return (
    <PageContainer>
      {isLoading && <FullPageLoader />}
      {showArenaForm ? (
        <div>
          <BackButton
            onClick={() => {
              setShowArenaForm(false);
              setSportsData({});
            }}
          />

          <AddUpdateArena
            setIsLoading={setIsLoading}
            sportData={sportData}
            fetchBookingAvailabiltyList={fetchBookingAvailabiltyList}
            setShowArenaForm={setShowArenaForm}
          />
        </div>
      ) : (
        <div>
          <PageHeading
            title={"Venue Operation"}
            Subtitle={"Smarter operations for smarter venues"}
          />
          <div className="bg-white min-h-screen rounded-md mt-5">
            <TabSwitcher tabs={VenueMenu} />
            <div className="md:px-6 px-3 pb-5">
              {/* Operational Hours */}
              {venueType == 1 && (
                <div className="">
                  <h1 className="md:text-xl text-md py-4">Operational Hours</h1>
                  <div className="grid grid-cols-7 h-20 bg-[#d9dce1a3] gap-1 md:gap-3 flex items-center px-2">
                    {venueAvailabilityData?.days?.map((item, index) => (
                      <div
                        onClick={() => {
                          let temp = [...venueAvailabilityData?.days];
                          temp[index].isTrue = !temp[index].isTrue;
                          setVenueAvailabilityData((data) => ({
                            ...data,
                            days: temp,
                          }));
                        }}
                        className={`text-xs md:text-sm ${
                          item?.isTrue ? "bg-[#2bace2] text-white" : "bg-white"
                        } h-12 cursor-pointer rounded-lg font-semibold w-auto  border-2 border-gray-300 flex items-center justify-center`}
                      >
                        {item?.label}
                      </div>
                    ))}
                  </div>

                  {/* Time Field */}
                  <div className="flex justify-center py-4 md:mt-3 mt-0">
                    <div className="md:border-2 border-gray-300  rounded-lg  w-full md:w-[600px] md:p-5 p-2">
                      <div className="md:text-lg text-base text-center ">
                        Select Opening & Closing Time
                      </div>
                      <div class="py-2 mb-5">
                        <HourSelect
                          value={venueAvailabilityData?.openingTime}
                          onChange={(e) =>
                            setVenueAvailabilityData((data) => ({
                              ...data,
                              openingTime: e.target.value,
                            }))
                          }
                        />
                        <HourSelect
                          value={venueAvailabilityData?.closingTime}
                          onChange={(e) =>
                            setVenueAvailabilityData((data) => ({
                              ...data,
                              closingTime: e.target.value,
                            }))
                          }
                        />
                      </div>

                      <Button
                        isLoading={isUpdateVenueLoading}
                        disabled={
                          isUpdateVenueLoading ||
                          venueAvailabilityData?.days?.every(
                            (day) => day.isTrue === false
                          ) ||
                          !venueAvailabilityData?.closingTime ||
                          !venueAvailabilityData?.openingTime
                        }
                        onClick={handleUpdateVenueAvailiability}
                        fullWidth
                        children={"Update"}
                      />
                    </div>
                  </div>
                </div>
              )}
              {/* Select sport  */}
              {venueType == 2 && (
                <div className="w-full">
                  <h1 className="md:text-xl text-md py-4">Select Sport</h1>
                  <div className="w-full md:w-96 mb-6">
                    <Input
                      label="Search Sport"
                      placeholder={"Search Sport"}
                      onChange={(e) => handleFilterSportsList(e)}
                    />
                  </div>

                  <div className="w-full md:px-2">
                    <div
                      className="grid gap-4"
                      style={{
                        gridTemplateColumns:
                          screenType == "mobile"
                            ? "repeat(auto-fill, minmax(5rem, 1fr))"
                            : "repeat(auto-fill, minmax(7rem, 1fr))",
                      }}
                    >
                      {sportsList?.map((item) => (
                        <div
                          key={item}
                          onClick={() => {
                            if (!item?.isDisable) {
                              dispatch(clearBookingAvailiabilityDetails());
                              setShowArenaForm(true);
                              setSportsData(item);
                            }
                          }}
                          style={{
                            background: item?.isDisable ? "#80808036" : "white",
                            cursor: item?.isDisable ? "not-allowed" : "pointer",
                          }}
                          className="h-28 rounded-md text-sm flex items-center justify-center border"
                        >
                          <div className="text-center flex flex-col items-center">
                            <span className="text-2xl">{item?.icon}</span>
                            <span className="mt-2 text-sm line-clamp-2">
                              {item?.name}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                    {sportsList?.length == 0 && (
                      <div className="h-[30vh] rounded-md bg-gray-100 font-semibold text-xl flex justify-center items-center py-3">
                        No Data Found
                      </div>
                    )}
                  </div>
                </div>
              )}
              {/* Booking history */}
              {venueType == 3 && (
                <>
                  <h1 className="md:text-xl text-md py-4">
                    Booking Availability
                  </h1>
                  {bookingAvailiabilityData?.data?.length > 0 ? (
                    <div className="grid md:grid-cols-2 gap-3 mt-2">
                      {bookingAvailiabilityData?.data?.map((item) => (
                        <div key={item?._id}>
                          <BookingHistory
                            item={item}
                            onDeleteClick={(id) => {
                              setDeleteId(id);
                              setDeleteDialog(true);
                            }}
                            onEditClick={(id) => {
                              dispatch(clearBookingAvailiabilityDetails());
                              handleBookingAvailabiltyDetail(id);
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="h-[30vh] rounded-md bg-gray-100 font-semibold text-xl flex justify-center items-center py-3">
                      No Data Found
                    </div>
                  )}
                  {bookingAvailiabilityData?.totalCount >
                    bookingAvailiabilityData?.data?.length && (
                    <div className="flex items-center justify-center my-8">
                      <Button
                        onClick={() =>
                          fetchBookingAvailabiltyList(
                            Math.ceil(
                              bookingAvailiabilityData?.data?.length / 10
                            ) + 1
                          )
                        }
                      >
                        View More
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
          {/* delete booking history dialog */}
          <CommonModal
            isFullPageLoading={isDeleteAccountLoading}
            icon={<ShieldQuestionMark />}
            isOpen={showDeleteDialog}
            confirmButtonText="Yes"
            cancleButtonText="No"
            onConfirm={handleDeleteBooking}
            title="Delete Booking"
            message="Are you sure you want to delete this booking availability?"
            onCancel={() => setDeleteDialog(false)}
          />
        </div>
      )}
      {/*  </div> */}
    </PageContainer>
  );
}

export default React.memo(VenueOperations);
